import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import AppShell from '@/components/layout/AppShell';
import SegmentedControl from '@/components/ui/SegmentedControl';
import MaterialIcon from '@/components/ui/MaterialIcon';
import benefitService from '@/services/benefitService';
import CoverageBanner from '@/components/coverage/CoverageBanner';
import { useAuthStore } from '@/store/authStore';
import { RelationshipType } from '@/types';

const RELATIONSHIP_LABEL: Record<string, string> = {
  [RelationshipType.SPOUSE]: 'Conjoint·e',
  [RelationshipType.CHILD]: 'Enfant',
  [RelationshipType.PARENT]: 'Parent',
  [RelationshipType.OTHER]: 'Autre',
};

const BenefitsPage: React.FC = () => {
  const { user } = useAuthStore();
  const [viewMode, setViewMode] = useState<'me' | 'family'>('me');

  const { data: benefitsData, isLoading } = useQuery({
    queryKey: ['benefits'],
    queryFn: () => benefitService.getUserBenefits(),
  });

  const coverageStatus = benefitsData?.status ?? 'active';
  const benefits = benefitsData?.benefits ?? [];

  const { data: familyMembers } = useQuery({
    queryKey: ['family-members'],
    queryFn: () => benefitService.getFamilyMembers(),
    enabled: viewMode === 'family',
  });

  const dependents = (familyMembers ?? []).filter((m) => m.id !== user?.id);

  const getCategoryColor = (_iconName: string) => {
    return 'bg-[#f0f4f2] text-emerald-700 dark:bg-emerald-900/30 dark:text-primary';
  };

  return (
    <AppShell title="Garanties">
      <div className="flex flex-col gap-6 pt-4">
        {coverageStatus !== 'active' && (
          <div className="px-4 md:px-0">
            <CoverageBanner status={coverageStatus} message={benefitsData?.statusMessage} />
          </div>
        )}

        <div className="flex flex-col gap-4 px-4 md:px-0">
          <p className="text-sm font-medium text-emerald-700 dark:text-primary">
            {user?.firstName} {user?.lastName} — Assuré principal
          </p>
          <SegmentedControl
            options={[
              { id: 'me', label: 'Moi' },
              { id: 'family', label: 'Ma Famille' },
            ]}
            value={viewMode}
            onChange={(val) => setViewMode(val as any)}
          />
        </div>

        {/* Family members (only in family mode) */}
        {viewMode === 'family' && (
          <div className="px-4 md:px-0">
            <h2 className="text-lg font-bold text-slate-900 dark:text-white mb-3">
              Ayants droit couverts
            </h2>
            {dependents.length === 0 ? (
              <p className="text-sm text-gray-500 dark:text-gray-400">
                Aucun ayant droit enregistré.
              </p>
            ) : (
              <ul className="flex flex-col gap-2">
                {dependents.map((d) => (
                  <li
                    key={d.id}
                    className="flex items-center gap-3 rounded-xl bg-white dark:bg-[#1a2c20] p-3 shadow-sm"
                  >
                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-primary font-semibold">
                      {d.firstName?.[0]}{d.lastName?.[0]}
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold text-slate-900 dark:text-white">
                        {d.firstName} {d.lastName}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400">
                        {d.relationshipType ? RELATIONSHIP_LABEL[d.relationshipType] ?? 'Ayant droit' : 'Ayant droit'}
                      </p>
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-3">
              Couverture identique au titulaire — mêmes garanties, mêmes plafonds.
            </p>
          </div>
        )}

        {/* Informational Banner */}
        <section className="px-4 md:px-0">
          <div className="relative overflow-hidden rounded-2xl bg-[#102216] p-5 text-white shadow-lg">
            <div aria-hidden="true" className="absolute right-0 top-0 -mr-4 -mt-4 h-24 w-24 rounded-full bg-primary opacity-20 blur-xl"></div>
            <div className="relative z-10 flex gap-4">
              <div className="flex-1">
                <h3 className="mb-1 text-base font-bold">Connaître vos droits</h3>
                <p className="text-sm font-light text-gray-300">
                  Vérifiez vos plafonds et taux de couverture pour mieux planifier vos soins de santé.
                </p>
              </div>
              <div className="flex items-center justify-center rounded-lg bg-white/10 p-2 backdrop-blur-sm h-fit">
                <MaterialIcon icon="info" className="text-primary" />
              </div>
            </div>
          </div>
        </section>

        <section className="px-4 md:px-0">
          <h2 className="text-lg font-bold text-slate-900 dark:text-white mb-3">
            Couverture par catégorie
          </h2>

          {isLoading ? (
            <div className="grid gap-3 md:grid-cols-2">
              {Array(4).fill(0).map((_, i) => (
                <div key={i} className="w-full h-20 bg-gray-100 dark:bg-white/5 rounded-xl animate-pulse" />
              ))}
            </div>
          ) : benefits.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {coverageStatus !== 'active'
                ? 'Vos garanties ne sont pas accessibles dans l\'état actuel de votre couverture.'
                : 'Aucune garantie disponible.'}
            </p>
          ) : (
            <ul className="grid gap-3 md:grid-cols-2">
              {benefits.map((benefit) => (
                <li key={benefit.id}>
                  <button
                    type="button"
                    aria-label={`${benefit.name}, couverture ${benefit.coveragePercentage}%`}
                    className="group flex w-full items-center justify-between rounded-xl bg-white p-4 shadow-sm transition-all active:scale-[0.98] dark:bg-[#1a2c20] hover:shadow-md"
                  >
                    <div className="flex items-center gap-4">
                      <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-full ${getCategoryColor(benefit.icon)}`}>
                        <MaterialIcon icon={benefit.icon} />
                      </div>
                      <div className="text-left">
                        <p className="text-base font-bold text-slate-900 dark:text-white">
                          {benefit.name}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                          {benefit.description} • <span className="text-emerald-600 dark:text-primary font-medium">{benefit.coveragePercentage}%</span>
                        </p>
                      </div>
                    </div>
                    <MaterialIcon
                      icon="chevron_right"
                      className="text-gray-400 group-hover:text-primary transition-colors"
                    />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
    </AppShell>
  );
};

export default BenefitsPage;
